import { useRef, useState } from "react";
import { Banknote } from "lucide-react";
import { Button } from "@/shared/components/shadcn/Button";
import { AmountInput } from "@/shared/components/ui/AmountInput";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { AppointmentChargeGuard } from "../components/AppointmentChargeGuard";
import { usePaymentMutations } from "../hooks/paymentQueries";
import {
  formatPaymentMoney, parsePaymentAmount, paymentAmountInput, paymentCurrencyDigits,
  type PaymentSummary, type RecordCashPaymentRequest,
} from "../types/paymentTypes";

function CashForm({ appointmentId, summary }: { appointmentId: string; summary: PaymentSummary }) {
  const { recordCash } = usePaymentMutations(appointmentId);
  const capture = useWorkspaceOperation();
  const { goToAppointmentDetails } = useAppNavigation();
  const [amount, setAmount] = useState(() => paymentAmountInput(summary.balanceMinor ?? 0, summary.currency));
  const attempt = useRef<RecordCashPaymentRequest | null>(null);
  const amountMinor = parsePaymentAmount(amount, summary.currency);
  const amountError = amountMinor === null ? `Enter a positive amount${summary.currency === "JPY" ? " with no decimal places" : " with up to 2 decimal places"}.` :
    amountMinor > (summary.balanceMinor ?? 0) ? "The amount cannot exceed the amount due." : undefined;
  const busy = recordCash.isPending;
  const locked = busy || !!attempt.current;
  function submit() {
    if (busy || amountError || !amountMinor) return;
    attempt.current ??= { amountMinor, expectedCurrency: summary.currency, idempotencyKey: crypto.randomUUID() };
    const isCurrent = capture();
    recordCash.mutate(attempt.current, {
      onSuccess: () => { if (isCurrent()) goToAppointmentDetails(appointmentId, true); },
    });
  }
  return <div className="space-y-4">
    <p className="text-sm text-muted-foreground">Amount due {formatPaymentMoney(summary.balanceMinor ?? 0, summary.currency)}.</p>
    <fieldset disabled={locked} className="min-w-0">
      <AmountInput label={`Cash received (${summary.currency})`} value={amount} onValueChange={setAmount} error={amountError} />
    </fieldset>
    {recordCash.error && <ErrorState message={recordCash.error.message} />}
    <Button className="w-full" disabled={busy || (!attempt.current && !!amountError)} onClick={submit}>
      <Banknote aria-hidden="true" />
      {recordCash.error ? "Retry" : `Record ${formatPaymentMoney(attempt.current?.amountMinor ?? amountMinor ?? 0, summary.currency)} cash`}
    </Button>
  </div>;
}

export function AppointmentCashChargePage() {
  return <AppointmentChargeGuard title="Cash">
    {({ appointmentId, summary }) =>
      !summary.canRecordCash || paymentCurrencyDigits(summary.currency) === null ?
        <ErrorState message={summary.cashUnavailableReason ?? "Cash cannot be recorded for this appointment."} /> :
        <CashForm key={`${appointmentId}:${summary.currency}`} appointmentId={appointmentId} summary={summary} />}
  </AppointmentChargeGuard>;
}
